import {flatMap} from 'lodash';
import React, {FC, useEffect, useMemo} from 'react';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {useBaseEditorContext} from '../../editors/baseEditorContext';
import {getEditorErrorLabel} from '../../editors/commons/editorErrors';
import {useExpressionsContext} from '../../editors/expressions/expressionsContext';
import {useRuleEditorContext} from '../../editors/ruleEditor/ruleEditorContext';
import {getConditionErrors} from '../ruleConditionHelpers';
import {ANDConditions} from '../ruleConditionModel';
import {TriggerWithConditions} from '../ruleTriggerHelpers';
import {RuleTriggerFromEventsDescription, RuleTriggersTypesEnum} from '../ruleTriggerModel';
import {RuleAssigneeIsChangedTriggerCard} from './cards/ruleAssigneeIsChangedTriggerCard';
import {RuleCommentAddedTriggerCard} from './cards/ruleCommentAddedTriggerCard';
import {RuleTagIsAddedTriggerCard} from './cards/ruleTagIsAddedTriggerCard';
import {RuleTagIsRemovedTriggerCard} from './cards/ruleTagIsRemovedTriggerCard';
import {RuleTeammateIsMentionedTriggerCard} from './cards/ruleTeammateIsMentionedTriggerCard';
import {EditorStateActions} from './hooks/useEditorState';
import {RuleMultilineParameterCard} from './ruleMultilineParameterCard';
import {RuleTriggerIcon} from './ruleTriggerIcon';

/*
 * Types.
 */

export interface RuleTriggerCardProps {
  triggerWithConditions: TriggerWithConditions;
  shouldHideError?: boolean;

  onUpdateTrigger: HandlerOf<TriggerWithConditions>;
  onRemoveTrigger?: HandlerOf<RuleTriggersTypesEnum>;
  onValidation: EditorStateActions['onValidation'];
}

/*
 * Component.
 */

export const RuleTriggerCard: FC<RuleTriggerCardProps> = (props) => {
  const localize = useLocalize();
  const {triggerWithConditions, shouldHideError, onUpdateTrigger, onRemoveTrigger, onValidation} = props;
  const {trigger} = triggerWithConditions;
  const {isReadOnly, hasHighlightErrorsFlag} = useBaseEditorContext();
  const {expressions} = useExpressionsContext();
  const {ruleIntegration} = useRuleEditorContext();

  const onUpdateConditions = (newConditions: ANDConditions) => {
    onUpdateTrigger({trigger, conditions: newConditions});
  };

  const onDelete = useMemo(
    () => (onRemoveTrigger ? () => onRemoveTrigger && onRemoveTrigger(trigger) : undefined),
    [onRemoveTrigger, trigger],
  );

  const maybeError = useMemo(
    () =>
      hasHighlightErrorsFlag
        ? flatMap(triggerWithConditions.conditions, (orConditions) =>
            flatMap(orConditions, (condition) =>
              getConditionErrors(condition, ruleIntegration.findConditionDefinition(condition), expressions),
            ),
          )[0]
        : undefined,
    [hasHighlightErrorsFlag, triggerWithConditions, ruleIntegration, expressions],
  );

  useEffect(() => onValidation(trigger, maybeError), [onValidation, trigger, maybeError]);

  const errorMessage = maybeError && !shouldHideError ? localize(getEditorErrorLabel(maybeError)) : undefined;

  switch (trigger) {
    case RuleTriggersTypesEnum.COMMENT:
      return (
        <RuleCommentAddedTriggerCard
          triggerWithConditions={triggerWithConditions}
          isDisabled={isReadOnly}
          errorMessage={errorMessage}
          onUpdateConditions={onUpdateConditions}
          onDelete={onDelete}
          expressions={expressions}
        />
      );

    case RuleTriggersTypesEnum.ASSIGN:
      return (
        <RuleAssigneeIsChangedTriggerCard
          triggerWithConditions={triggerWithConditions}
          errorMessage={errorMessage}
          expressions={expressions}
          onUpdateConditions={onUpdateConditions}
          onDelete={onDelete}
        />
      );

    case RuleTriggersTypesEnum.MENTION:
      return (
        <RuleTeammateIsMentionedTriggerCard
          triggerWithConditions={triggerWithConditions}
          errorMessage={errorMessage}
          expressions={expressions}
          onUpdateConditions={onUpdateConditions}
          onDelete={onDelete}
        />
      );

    case RuleTriggersTypesEnum.TAG:
      return (
        <RuleTagIsAddedTriggerCard
          triggerWithConditions={triggerWithConditions}
          errorMessage={errorMessage}
          expressions={expressions}
          onUpdateConditions={onUpdateConditions}
          onDelete={onDelete}
        />
      );

    case RuleTriggersTypesEnum.UNTAG:
      return (
        <RuleTagIsRemovedTriggerCard
          triggerWithConditions={triggerWithConditions}
          errorMessage={errorMessage}
          expressions={expressions}
          onUpdateConditions={onUpdateConditions}
          onDelete={onDelete}
        />
      );

    default:
      return (
        <RuleMultilineParameterCard
          title={localize(RuleTriggerFromEventsDescription[trigger].title)}
          icon={<RuleTriggerIcon trigger={trigger} />}
          errorMessage={errorMessage}
          onCancel={onDelete}
        />
      );
  }
};
